<template>
    <header class="top-bar" :style="{ '--top-bar-fade': fadeOpacity }">
        <button
            type="button"
            class="top-bar-burger d-md-none"
            :title="$t('topBar.openMenu')"
            @click="toggleSideNav()"
        >
            <inline-svg :src="require('@/assets/icons/menu-bars.svg')"/>
        </button>

        <transition name="top-bar-heading" mode="out-in">
            <div :key="pageTitle" class="top-bar-heading">
                <span v-if="pageIcon" class="top-bar-icon">
                    <inline-svg :src="require(`@/assets/icons/${pageIcon}`)"/>
                </span>
                <h1 class="top-bar-title">{{ pageTitle }}</h1>
            </div>
        </transition>

        <div class="top-bar-actions">
            <button
                type="button"
                class="top-bar-search"
                :title="$t('topBar.searchPlaceholder')"
                @click="requestQuickMenu()"
            >
                <inline-svg class="top-bar-search-icon" :src="require('@/assets/icons/search.svg')"/>
                <span class="top-bar-search-text">{{ $t('topBar.searchPlaceholder') }}</span>
                <span class="top-bar-shortcut">{{ shortcutLabel }}</span>
            </button>

            <span class="top-bar-divider"></span>

            <AbsoluteMenu right-side>
                <template #button="{ toggle, isOpen }">
                    <button
                        type="button"
                        class="top-bar-profile"
                        :class="{ 'is-open': isOpen }"
                        :title="$t('topBar.account')"
                        @click.stop="toggle()"
                    >
                        <span class="top-bar-avatar" :style="{ backgroundColor: avatarColor }">{{ initial }}</span>
                        <span class="top-bar-profile-text">
                            <span class="top-bar-profile-name">{{ username }}</span>
                            <span v-if="organizationName" class="top-bar-profile-org">{{ organizationName }}</span>
                        </span>
                    </button>
                </template>
                <template #menu="{ close }">
                    <MenuItem
                        v-if="canOpenSettings"
                        icon="settings.svg"
                        :label="$t('navBar.settings')"
                        @click="close(); goToSettings()"
                    />
                    <MenuItem
                        icon="zap.svg"
                        :label="$t('topBar.interfaceSettings')"
                        @click="close(); goToInterfaceSettings()"
                    />
                    <MenuItem
                        icon="log-out.svg"
                        tone="danger"
                        :label="$t('navBar.logout')"
                        @click="close(); logout()"
                    />
                </template>
            </AbsoluteMenu>
        </div>
    </header>
</template>

<script>
import {INTERFACE_SETTINGS_PANEL} from "@/helpers/SettingsTabs";
import {mapActions, mapMutations, mapState} from 'vuex';
import AbsoluteMenu from '@/views/components/AbsoluteMenu.vue';
import MenuItem from '@/views/components/MenuItem.vue';
import {NavLinksHelper} from '@/helpers/NavLinksHelper';
import {colorUtil} from '@/helpers/ColorUtil';
import {isMacPlatform} from '@/helpers/platform';

const FADE_SCROLL_DISTANCE = 60;

export default {
    name: 'TopBar',
    components: {AbsoluteMenu, MenuItem},
    data() {
        return {
            fadeOpacity: 0,
            scrollFrame: 0
        };
    },
    props: {
        title: {
            type: String,
            default: ''
        },
        icon: {
            type: String,
            default: ''
        }
    },
    computed: {
        ...mapState('account', ['user']),
        currentLink() {
            return NavLinksHelper.findByPath(this.$route.path);
        },
        canOpenSettings() {
            return NavLinksHelper.allowedLinks(this.user, this.isPhone()).some(link => link.id === "ACCOUNTS_SETTINGS");
        },
        pageTitle() {
            if (this.title) return this.title;
            if (this.$route.meta?.title) return this.$t(this.$route.meta.title);
            if (this.currentLink) return this.$t(this.currentLink.text);
            return this.organizationName;
        },
        pageIcon() {
            if (this.icon) return this.icon;
            if (this.$route.meta?.icon) return this.$route.meta.icon;
            if (this.currentLink) return this.currentLink.icon;
            return 'home.svg';
        },
        username() {
            return this.user?.username || '';
        },
        organizationName() {
            return this.user?.organization?.name || '';
        },
        initial() {
            return this.username.charAt(0).toUpperCase();
        },
        shortcutLabel() {
            return isMacPlatform() ? '⌘K' : 'Ctrl+K';
        },
        avatarColor() {
            if (!this.username) return null;
            return colorUtil.randomHSL(this.username.charCodeAt(0));
        }
    },
    watch: {
        '$route.fullPath'() {
            this.fadeOpacity = 0;
        }
    },
    mounted() {
        window.addEventListener('scroll', this.onContentScroll, true);
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.onContentScroll, true);
        if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    },
    methods: {
        ...mapActions('account', ['logout']),
        onContentScroll(event) {
            const target = event.target;
            if (!target?.classList?.contains('main-router')) return;
            if (this.scrollFrame) return;

            this.scrollFrame = window.requestAnimationFrame(() => {
                this.scrollFrame = 0;
                this.fadeOpacity = Math.min(Math.max(target.scrollTop, 0) / FADE_SCROLL_DISTANCE, 1);
            });
        },
        ...mapMutations('global', ['toggleSideNav', 'requestQuickMenu']),
        goToSettings() {
            this.$router.push({name: 'MySettingsPage', params: {panel: 0}});
        },
        goToInterfaceSettings() {
            this.$router.push({name: 'MySettingsPage', params: {panel: INTERFACE_SETTINGS_PANEL}});
        }
    }
}
</script>

<style lang="scss" scoped>
.top-bar {
    position: relative;
    display: flex;
    align-items: center;
    gap: $default-edge-spacing-l;
    flex: 0 0 auto;
    margin: 0 $page-gutter;
    padding: 0;

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        opacity: var(--top-bar-fade, 0);
        transition: opacity 0.12s linear;

        @include fade-bottom;
    }

    @include mobile {
        gap: $default-edge-spacing;
        margin: 0 $page-gutter-mobile;
        padding: 0;
    }
}

.top-bar-burger {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    padding: 0;
    border: 0;
    background-color: transparent;
    color: $color-primary-text;
    cursor: pointer;

    svg {
        width: 20px;
        height: 20px;
    }

    &:hover {
        transform: none;
        opacity: 1;
    }
}

.top-bar-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    border-radius: $radius-small;
    background-color: $color-input-back;
    color: $color-primary-blue;

    svg {
        width: 18px;
        height: 18px;
    }

    :deep(svg[stroke]:not([stroke="none"])),
    :deep(svg [stroke]:not([stroke="none"])) {
        stroke: currentColor;
    }

    :deep(svg[fill]:not([fill="none"])),
    :deep(svg [fill]:not([fill="none"])) {
        fill: currentColor;
    }

    @include mobile {
        display: none;
    }
}

.top-bar-heading {
    display: flex;
    align-items: center;
    gap: $default-edge-spacing-l;
    min-width: 0;
    transform-origin: left center;

    @include mobile {
        gap: $default-edge-spacing;
    }
}

.top-bar-heading-enter-active {
    animation: top-bar-heading-pop 0.3s cubic-bezier(0.34, 1.3, 0.64, 1) 0.14s backwards;
}

.top-bar-heading-leave-active {
    transition: opacity 0.12s ease, transform 0.12s ease;
}

.top-bar-heading-leave-to {
    opacity: 0;
    transform: translateY(-4px) scale(0.96);
}

@keyframes top-bar-heading-pop {
    0% {
        opacity: 0;
        transform: translateY(6px) scale(0.9);
    }

    50% {
        opacity: 1;
    }

    100% {
        opacity: 1;
        transform: none;
    }
}

@media (prefers-reduced-motion: reduce) {
    .top-bar-heading-enter-active {
        animation: none;
    }

    .top-bar-heading-leave-active {
        transition: none;
    }
}

.top-bar-title {
    margin: 0;
    min-width: 0;
    font-size: $font-title-big;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: $color-white-text;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @include mobile {
        font-size: $font-title;
    }
}

.top-bar-actions {
    display: flex;
    align-items: center;
    gap: $default-edge-spacing;
    margin-left: auto;
}

.top-bar-search {
    display: inline-flex;
    align-items: center;
    gap: $default-edge-spacing-l;
    width: 300px;
    height: $control-height;
    padding: 0 $default-edge-spacing 0 $default-edge-spacing-l;
    border: 1px solid $color-input-back !important;
    border-radius: $radius-small;
    background-color: transparent;
    color: $color-secondary-text;
    cursor: pointer;
    transition: border-color 0.15s ease;

    .top-bar-search-icon {
        width: 17px;
        height: 17px;
        flex: 0 0 17px;
        stroke: currentColor;
    }

    .top-bar-search-text {
        flex: 1 1 auto;
        min-width: 0;
        font-size: $font-subtitle;
        color: $color-secondary-text;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &:hover {
        transform: none;
        opacity: 1;
    }

    @include desktop {
        &:hover {
            border-color: $color-primary-blue !important;
        }
    }

    &:focus-visible {
        outline: none;
        border-color: $color-primary-blue !important;
        box-shadow: 0 0 0 3px rgba($color-primary-blue, 0.2);
    }

    @include mobile {
        width: $top-bar-avatar-size;
        height: $top-bar-avatar-size;
        gap: 0;
        padding: 0;
        justify-content: center;

        .top-bar-search-text,
        .top-bar-shortcut {
            display: none;
        }
    }
}

.top-bar-shortcut {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    height: 24px;
    padding: 0 $default-edge-spacing-s;
    border-radius: 6px;
    background-color: $color-input-back;
    color: $color-primary-text;
    font-size: $font-small;
    font-weight: 500;
    letter-spacing: 0.02em;
}


.top-bar-divider {
    width: 1px;
    height: 24px;
    flex: 0 0 1px;
    margin: 0 $default-edge-spacing;

    background-color: $color-input-back;
}

@media (min-width: 769px) {
  .top-bar-divider {
    margin-left: 12px !important;
  }
}
.top-bar-profile {
    display: inline-flex;
    align-items: center;
    gap: $default-edge-spacing;
    height: $control-height;
    max-width: 210px;
    padding: 0 $default-edge-spacing-l 0 $default-edge-spacing-xs;
    border: 1px solid transparent !important;
    border-radius: 999px;
    background-color: transparent;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;

    &:hover {
        transform: none;
        opacity: 1;
    }

    @include desktop {
        &:hover,
        &.is-open {
            border-color: $color-primary-blue !important;
            background-color: $color-input-back-darker-solid;
        }
    }

    &:focus-visible {
        outline: none;
        border-color: $color-primary-blue !important;
        box-shadow: 0 0 0 3px rgba($color-primary-blue, 0.25);
    }

    @include mobile {
        max-width: none;
        height: $top-bar-avatar-size;
        padding: 0;

        .top-bar-profile-text {
            display: none;
        }
    }
}

.top-bar-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: $top-bar-avatar-size;
    height: $top-bar-avatar-size;
    flex: 0 0 $top-bar-avatar-size;
    border-radius: 50%;
    font-size: $font-small;
    font-weight: 600;
    color: $color-primary-back;

}

.top-bar-profile-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    line-height: 1.2;
}

.top-bar-profile-name {
    max-width: 130px;
    font-size: $font-subtitle;
    font-weight: 500;
    color: $color-white-text;
    text-transform: capitalize;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.top-bar-profile-org {
    max-width: 130px;
    font-size: $font-small;
    color: $color-secondary-text;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
